import { useEffect, useRef, useState } from "react";
import themeUrl from "../assets/sounds/theme.mp3";

const STORAGE_KEY = "cap.music";

/**
 * Compact music toggle. Persists preference in localStorage.
 * Won't autoplay (browsers block it) until the user toggles it on.
 */
export function MusicToggle({ className }: { className?: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const a = new Audio(themeUrl);
    a.loop = true;
    a.volume = 0.35;
    audioRef.current = a;
    const wanted = localStorage.getItem(STORAGE_KEY) === "on";
    if (wanted) {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
    return () => {
      a.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      localStorage.setItem(STORAGE_KEY, "off");
    } else {
      a.play()
        .then(() => {
          setPlaying(true);
          localStorage.setItem(STORAGE_KEY, "on");
        })
        .catch(() => setPlaying(false));
    }
  };

  return (
    <button
      onClick={toggle}
      aria-label={playing ? "Mute music" : "Play music"}
      title={playing ? "Mute" : "Play"}
      className={[
        "inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs uppercase tracking-widest text-white/80 hover:bg-white/10",
        className ?? "",
      ].join(" ")}
    >
      <span aria-hidden="true">{playing ? "♪" : "✕"}</span>
      {playing ? "Music On" : "Music Off"}
    </button>
  );
}
